import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import CopyButton from './CopyButton';
import './AdminNewsletter.css';

const ORDER = ['delivered', 'opened', 'clicked', 'soft_bounce', 'bounced', 'spam', 'unsubscribed', 'unknown'];

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Уведомления SMTP.BZ (webhook): адрес для кабинета SMTP.BZ и что уже пришло.
function AdminMailWebhook() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [openRaw, setOpenRaw] = useState(null);

    const load = useCallback(() => api.get('/admin/mail-webhook')
        .then((res) => { setData(res.data); setError(null); })
        .catch((err) => setError(getApiErrorMessage(err, 'Не удалось загрузить данные'))), []);

    useEffect(() => { load(); }, [load]);

    const rotate = async () => {
        if (!window.confirm('Сделать новый адрес? Старый перестанет работать — его нужно будет заменить в кабинете SMTP.BZ.')) return;
        setBusy(true);
        try {
            const res = await api.post('/admin/mail-webhook/rotate');
            setData(res.data);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось сменить адрес'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="admin-main-card">
            <div className="nl-head">
                <h2 className="admin-main-card__title">Статусы доставки</h2>
                <button type="button" className="btn btn--secondary btn--sm" onClick={load}>Обновить</button>
            </div>
            <div className="nl-body">
                <p className="nl-hint">
                    SMTP.BZ может сам сообщать СВОД, что стало с письмом: доставлено, открыто, ящик не существует,
                    получатель нажал «Это спам». Статусы появятся в журнале писем и в отчётах рассылок. Тем, чей ящик
                    не существует или кто пожаловался на спам, рассылка отключается автоматически — это бережёт
                    репутацию отправителя. Коды подтверждения и сброс пароля таким людям по-прежнему уходят.
                </p>
                {error && <div className="admin-notice admin-notice--error">{error}</div>}
                {data && (
                    <>
                        <div className="nl-block">
                            <h3 className="nl-block__title"><span className="nl-step">1</span> Адрес для SMTP.BZ</h3>
                            <div className="mai-row form-group whook-url">
                                <input type="text" readOnly value={data.url} aria-label="Адрес для уведомлений" onFocus={(e) => e.target.select()} />
                                <CopyButton text={data.url} className="btn btn--primary" />
                            </div>
                            <p className="nl-hint nl-hint--small">
                                Адрес секретный: по нему СВОД узнаёт, что уведомление пришло от SMTP.BZ. Не публикуйте его.
                                Если он стал известен посторонним — <button type="button" className="nl-link" onClick={rotate} disabled={busy}>сделайте новый</button>.
                            </p>
                        </div>
                        <div className="nl-block">
                            <h3 className="nl-block__title"><span className="nl-step">2</span> Включите уведомления в SMTP.BZ</h3>
                            <ol className="whook-steps">
                                <li>Войдите в личный кабинет SMTP.BZ и откройте настройки API (раздел Webhooks / Уведомления).</li>
                                <li>Вставьте адрес выше в поле URL.</li>
                                <li>Отметьте события: доставка, ошибка/возврат (bounce), открытие, клик, спам (жалоба), отписка.</li>
                                <li>Сохраните. Затем отправьте себе проверочное письмо во вкладке «Настройки» — через минуту здесь появится уведомление.</li>
                            </ol>
                        </div>
                        <div className="nl-block">
                            <h3 className="nl-block__title"><span className="nl-step">3</span> Что пришло</h3>
                            <p className="nl-hint">
                                {data.last_event_at
                                    ? <>Последнее уведомление: <strong>{formatDate(data.last_event_at)}</strong>.</>
                                    : <>Уведомлений пока не было.</>}
                                {data.unmatched_30d > 0 && <> Не связано с письмами из журнала за 30 дней: {data.unmatched_30d}.</>}
                            </p>
                            {Object.keys(data.counts_30d || {}).length > 0 && (
                                <div className="nl-stats">
                                    {ORDER.filter((k) => data.counts_30d[k]).map((k) => (
                                        <div key={k} className={`nl-stat ${['bounced', 'spam'].includes(k) ? 'nl-stat--bad' : ''}`}>
                                            <span>{data.labels[k]} · 30 дней</span>
                                            <strong>{data.counts_30d[k]}</strong>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {data.recent.length > 0 && (
                                <div className="admin-table-scroll">
                                    <table className="nl-table">
                                        <thead><tr><th>Получено</th><th>Событие</th><th>Адрес</th><th>Подробности</th><th aria-label="Исходные данные" /></tr></thead>
                                        <tbody>
                                            {data.recent.map((e) => (
                                                <React.Fragment key={e.id}>
                                                    <tr>
                                                        <td className="mlog-time">{formatDate(e.received_at)}</td>
                                                        <td>
                                                            <span className={`nl-rstatus whook-status--${e.status}`}>{e.status_label}</span>
                                                            {e.event && e.event !== e.status && <div className="nl-muted mlog-id">{e.event}</div>}
                                                        </td>
                                                        <td className="mlog-to">{e.email || '—'}{!e.matched && <div className="nl-muted mlog-id">письмо не найдено в журнале</div>}</td>
                                                        <td className="mlog-subject">{e.details || '—'}</td>
                                                        <td>
                                                            {(e.raw || e.headers) && (
                                                                <button type="button" className="nl-link" onClick={() => setOpenRaw(openRaw === e.id ? null : e.id)}>
                                                                    {openRaw === e.id ? 'скрыть' : 'как пришло'}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {openRaw === e.id && (
                                                        <tr>
                                                            <td colSpan={5}>
                                                                <div className="whook-raw-label">Данные</div>
                                                                <pre className="whook-raw">{e.raw}</pre>
                                                                {e.headers && (
                                                                    <>
                                                                        <div className="whook-raw-label">Заголовки{e.source_ip ? ` · пришло с адреса ${e.source_ip}` : ''}</div>
                                                                        <pre className="whook-raw">{e.headers}</pre>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}

export default AdminMailWebhook;
